export interface Lesson {
  id: string;
  title: string;
  thumbnail: string;
  gradeBand: 'Elementary' | 'Middle School' | 'High School';
  grades: string;
  topic: string;
  sdgs: number[];
  duration: string;
  description: string;
  isFeatured: boolean;
  isNew: boolean;
  isFree: boolean;
  price?: number;
  downloads: number;
  standards: string[];
  essentialQuestion: string;
  objectives: string[];
  materials: string[];
}

export interface Module {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  gradeBand: 'Elementary' | 'Middle School' | 'High School' | 'All Grades';
  lessonCount: number;
  duration: string;
  topics: string[];
  sdgs: number[];
  price: number;
  lessons: string[];
  color: string;
}

export const LESSONS: Lesson[] = [
{
  id: 'lesson-001',
  title: 'What Is Global Citizenship? An Introduction for Young Learners',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1ae3459-1767539134231.png",
  gradeBand: 'Elementary',
  grades: 'Grades 3–5',
  topic: 'Global Citizenship',
  sdgs: [4, 10, 16],
  duration: '1 Day',
  description:
  'Students explore what it means to be a citizen of the world through storytelling, maps, and collaborative discussion activities.',
  isFeatured: true,
  isNew: false,
  isFree: true,
  downloads: 3847,
  standards: ['CCSS.ELA-LITERACY.RI.4.1', 'NCSS Theme I: Culture'],
  essentialQuestion: 'What responsibilities do we have as members of a global community?',
  objectives: [
  'Define global citizenship in age-appropriate terms',
  'Identify connections between their local community and the wider world',
  'Recognize that their choices affect people in other countries'],

  materials: ['World map poster', 'Identity circle worksheet', 'Story: The Name Jar']
},
{
  id: 'lesson-002',
  title: 'Clean Water Access: A Global Crisis in Our Classrooms',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1954cf168-1767868302378.png",
  gradeBand: 'Middle School',
  grades: 'Grades 6–8',
  topic: 'Sustainability',
  sdgs: [6, 13, 17],
  duration: '3 Days',
  description:
  'A project-based unit where students investigate global water inequality, analyze data from WHO reports, and design community solutions.',
  isFeatured: true,
  isNew: false,
  isFree: false,
  price: 8,
  downloads: 2104,
  standards: ['NGSS MS-ESS3-3', 'C3 Framework D2.Geo.9.6-8'],
  essentialQuestion: 'How does access to clean water shape a community\'s future?',
  objectives: [
  'Analyze global water access data using infographics',
  'Compare water use across high- and low-income nations',
  'Propose evidence-based solutions to local water challenges'],

  materials: ['WHO data handout', 'Water footprint calculator', 'Design brief template']
},
{
  id: 'lesson-003',
  title: 'Identity Mapping: Who Am I in the World?',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1cfcbac-1776635145203.png",
  gradeBand: 'Middle School',
  grades: 'Grades 6–8',
  topic: 'Identity & Culture',
  sdgs: [5, 10, 16],
  duration: '2 Days',
  description:
  'Students create personal identity maps exploring race, culture, language, and belonging, then share in structured dialogue circles.',
  isFeatured: false,
  isNew: true,
  isFree: false,
  price: 6,
  downloads: 1567,
  standards: ['CCSS.ELA-LITERACY.SL.7.1', 'SEL Standard: Self-Awareness'],
  essentialQuestion: 'How do our identities shape how we experience the world?',
  objectives: [
  'Identify multiple dimensions of personal identity',
  'Recognize how social identities intersect',
  'Practice perspective-taking through peer dialogue'],

  materials: ['Identity wheel template', 'Dialogue protocol cards', 'Reflection journal']
},
{
  id: 'lesson-004',
  title: 'Climate Justice: Who Bears the Burden?',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_113436b75-1769325541632.png",
  gradeBand: 'High School',
  grades: 'Grades 9–12',
  topic: 'Global Issues',
  sdgs: [13, 10, 17],
  duration: '1 Week',
  description:
  'An interdisciplinary unit examining climate change through a justice lens — who is most affected, why, and what can be done.',
  isFeatured: true,
  isNew: false,
  isFree: false,
  price: 12,
  downloads: 4231,
  standards: ['AP Environmental Science', 'CCSS.ELA-LITERACY.RH.9-10.8'],
  essentialQuestion: 'Why do marginalized communities bear a disproportionate burden of climate impacts?',
  objectives: [
  'Analyze climate vulnerability maps by region and income',
  'Evaluate policy responses from multiple stakeholder perspectives',
  'Construct a written argument for climate justice action'],

  materials: ['Climate Vulnerability Index data', 'Case studies: Bangladesh, Maldives, Louisiana', 'Policy brief template']
},
{
  id: 'lesson-005',
  title: 'Refugee Journeys: Stories of Resilience and Hope',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9ed37e5-1776635144601.png",
  gradeBand: 'Middle School',
  grades: 'Grades 6–8',
  topic: 'Human Rights',
  sdgs: [10, 16, 17],
  duration: '2 Days',
  description:
  'Students engage with real refugee narratives through multimedia, build empathy through perspective-taking activities, and explore UNHCR data.',
  isFeatured: false,
  isNew: true,
  isFree: false,
  price: 7,
  downloads: 1893,
  standards: ['CCSS.ELA-LITERACY.RI.7.6', 'NCSS Theme IV: Individual Development'],
  essentialQuestion: 'What does it mean to be displaced, and how can communities respond with dignity?',
  objectives: [
  'Describe the circumstances that cause forced displacement',
  'Analyze personal narratives for themes of resilience',
  'Evaluate international responses to the refugee crisis'],

  materials: ['UNHCR fact sheet', 'Story excerpts anthology', 'Empathy mapping worksheet']
},
{
  id: 'lesson-006',
  title: 'Food Systems & Global Hunger: From Farm to Fork',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_142abf69e-1776635144818.png",
  gradeBand: 'Elementary',
  grades: 'Grades 3–5',
  topic: 'Sustainability',
  sdgs: [2, 12, 15],
  duration: '3 Days',
  description:
  'Young learners trace food from its origins around the world, learn about food waste, and design their own "zero-waste" lunch plan.',
  isFeatured: false,
  isNew: false,
  isFree: true,
  downloads: 2756,
  standards: ['Next Generation Science Standards K-ESS3-1', 'CCSS.MATH.CONTENT.3.MD'],
  essentialQuestion: 'Where does our food come from, and how can we make food choices that help the planet?',
  objectives: [
  'Trace the journey of a food item from its country of origin',
  'Identify causes and impacts of global food waste',
  'Design a sustainable meal plan with reasoning'],

  materials: ['World food map', 'Food waste infographic', 'Meal design worksheet']
},
{
  id: 'lesson-007',
  title: 'Gender Equality in Education: A Global Perspective',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d3468ba7-1776635145543.png",
  gradeBand: 'High School',
  grades: 'Grades 9–12',
  topic: 'Human Rights',
  sdgs: [4, 5, 10],
  duration: '3 Days',
  description:
  'Students investigate global disparities in educational access by gender, examine structural causes, and advocate for change through research projects.',
  isFeatured: false,
  isNew: false,
  isFree: false,
  price: 10,
  downloads: 1428,
  standards: ['AP Human Geography', 'CCSS.ELA-LITERACY.W.9-10.7'],
  essentialQuestion: 'How do gender norms and systemic barriers limit educational access globally?',
  objectives: [
  'Analyze UNESCO data on gender parity in education',
  'Identify structural and cultural barriers to girls\' education',
  'Create an advocacy campaign for educational equity'],

  materials: ['UNESCO Gender Parity Index data', 'Case studies: Afghanistan, Nigeria, India', 'Advocacy project guide']
},
{
  id: 'lesson-008',
  title: 'The Language of Belonging: Multilingualism and Identity',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8f29e00-1776635146105.png",
  gradeBand: 'Elementary',
  grades: 'Grades 3–5',
  topic: 'Identity & Culture',
  sdgs: [4, 10],
  duration: '1 Day',
  description:
  'Students celebrate linguistic diversity in their classroom, learn greetings in six languages, and explore how language connects us to culture and family.',
  isFeatured: false,
  isNew: true,
  isFree: true,
  downloads: 3102,
  standards: ['CCSS.ELA-LITERACY.SL.3.1', 'WIDA ELD Standards'],
  essentialQuestion: 'How does the language we speak shape our sense of belonging?',
  objectives: [
  'Recognize and celebrate multilingualism in their community',
  'Connect language to cultural identity and heritage',
  'Practice inclusive communication strategies'],

  materials: ['Language map poster', 'Greeting cards in 6 languages', 'Family language interview guide']
},
{
  id: 'lesson-009',
  title: 'Economic Inequality: Why Does It Matter?',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa6c0597-1768549574899.png",
  gradeBand: 'High School',
  grades: 'Grades 9–12',
  topic: 'Global Issues',
  sdgs: [1, 8, 10],
  duration: '1 Week',
  description:
  'A data-rich unit on global wealth distribution, Gini coefficients, and structural causes of inequality, culminating in a policy proposal.',
  isFeatured: false,
  isNew: false,
  isFree: false,
  price: 14,
  downloads: 987,
  standards: ['AP Economics', 'C3 Framework D2.Eco.13.9-12'],
  essentialQuestion: 'What are the causes and consequences of extreme wealth inequality?',
  objectives: [
  'Interpret Gini coefficient data across nations',
  'Evaluate competing explanations for economic inequality',
  'Develop a policy proposal to reduce inequality in one country'],

  materials: ['World Bank inequality data', 'Gini coefficient explainer', 'Policy proposal template']
},
{
  id: 'lesson-010',
  title: 'Plastic Pollution: Our Ocean, Our Responsibility',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1be1bdf2c-1776635146888.png",
  gradeBand: 'Middle School',
  grades: 'Grades 6–8',
  topic: 'Sustainability',
  sdgs: [14, 12, 13],
  duration: '2 Days',
  description:
  'Students investigate the lifecycle of plastic, map ocean pollution hotspots, and create public service announcements for their school community.',
  isFeatured: false,
  isNew: false,
  isFree: false,
  price: 7,
  downloads: 2341,
  standards: ['NGSS MS-LS2-5', 'CCSS.ELA-LITERACY.W.7.2'],
  essentialQuestion: 'How does our daily plastic use connect to ocean ecosystems on the other side of the world?',
  objectives: [
  'Trace the journey of plastic from production to ocean',
  'Analyze ocean pollution data and identify major sources',
  'Create a persuasive PSA campaign targeting behavior change'],

  materials: ['Ocean plastic map', 'Plastic lifecycle diagram', 'PSA storyboard template']
},
{
  id: 'lesson-011',
  title: 'Indigenous Knowledge & Environmental Stewardship',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1722b2741-1776635145579.png",
  gradeBand: 'High School',
  grades: 'Grades 9–12',
  topic: 'Sustainability',
  sdgs: [15, 13, 16],
  duration: '3 Days',
  description:
  'Students examine how indigenous communities around the world have sustainably managed ecosystems for centuries and what modern society can learn.',
  isFeatured: false,
  isNew: true,
  isFree: false,
  price: 11,
  downloads: 743,
  standards: ['AP Environmental Science', 'C3 Framework D2.His.1.9-12'],
  essentialQuestion: 'What can indigenous ecological knowledge teach us about sustainable living?',
  objectives: [
  'Analyze case studies of indigenous land management practices',
  'Compare indigenous and Western scientific approaches to ecology',
  'Evaluate how indigenous rights connect to environmental protection'],

  materials: ['Case studies: Amazon, Pacific Northwest, Sahel', 'Comparative analysis framework', 'Guest speaker guide']
},
{
  id: 'lesson-012',
  title: 'Migration Stories: Understanding Why People Move',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1b523d3c8-1772158735168.png",
  gradeBand: 'Elementary',
  grades: 'Grades 3–5',
  topic: 'Global Citizenship',
  sdgs: [10, 16],
  duration: '2 Days',
  description:
  'Through picture books, personal narratives, and mapping activities, students explore push/pull factors of migration and build empathy for newcomers.',
  isFeatured: false,
  isNew: false,
  isFree: true,
  downloads: 2987,
  standards: ['NCSS Theme II: Time, Continuity, Change', 'CCSS.ELA-LITERACY.RL.4.3'],
  essentialQuestion: 'Why do people leave their homes, and how do we welcome those who come to ours?',
  objectives: [
  'Identify push and pull factors that drive migration',
  'Analyze migration patterns using maps and data',
  'Practice empathy through first-person narrative writing'],

  materials: ['Migration push/pull chart', 'Picture book: The Journey', 'Personal narrative template']
}];


export const MODULES: Module[] = [
{
  id: 'module-001',
  title: 'Global Citizenship Foundations',
  description:
  'Build the conceptual foundation for global citizenship education. Students develop shared vocabulary, explore their place in the world, and begin connecting local and global issues.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1513740da-1774862546708.png",
  gradeBand: 'All Grades',
  lessonCount: 6,
  duration: '3 Weeks',
  topics: ['Global Citizenship', 'Identity & Culture', 'Human Rights'],
  sdgs: [4, 10, 16, 17],
  price: 38,
  color: 'from-accent-500 to-accent-700',
  lessons: ['lesson-001', 'lesson-008', 'lesson-012', 'lesson-003', 'lesson-005', 'lesson-007']
},
{
  id: 'module-002',
  title: 'Identity & Culture Across Borders',
  description:
  'Deepen students\' understanding of cultural identity, intersectionality, and belonging. Includes personal narrative writing, dialogue protocols, and cross-cultural comparisons.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18697be8d-1770356797892.png",
  gradeBand: 'Middle School',
  lessonCount: 5,
  duration: '2 Weeks',
  topics: ['Identity & Culture', 'Human Rights', 'Global Citizenship'],
  sdgs: [5, 10, 16],
  price: 32,
  color: 'from-primary-500 to-primary-700',
  lessons: ['lesson-003', 'lesson-008', 'lesson-005', 'lesson-007', 'lesson-001']
},
{
  id: 'module-003',
  title: 'SDGs in Action: Sustainability & Justice',
  description:
  'Connect students directly to the UN Sustainable Development Goals through hands-on investigations, data analysis, and community action projects.',
  thumbnail: "https://images.unsplash.com/photo-1565365655538-f7caef0362bf",
  gradeBand: 'High School',
  lessonCount: 7,
  duration: '4 Weeks',
  topics: ['Sustainability', 'Global Issues', 'Human Rights'],
  sdgs: [1, 2, 6, 13, 14, 15],
  price: 45,
  color: 'from-terracotta-500 to-terracotta-700',
  lessons: ['lesson-004', 'lesson-002', 'lesson-006', 'lesson-010', 'lesson-011', 'lesson-009', 'lesson-007']
},
{
  id: 'module-004',
  title: 'Our Planet, Our Choices',
  description:
  'An elementary-friendly introduction to sustainability, food systems, and environmental responsibility. Project-based and hands-on throughout.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_19de02ab7-1774528380180.png",
  gradeBand: 'Elementary',
  lessonCount: 4,
  duration: '2 Weeks',
  topics: ['Sustainability', 'Global Citizenship'],
  sdgs: [2, 12, 14, 15],
  price: 24,
  color: 'from-emerald-500 to-emerald-700',
  lessons: ['lesson-006', 'lesson-001', 'lesson-012', 'lesson-008']
},
{
  id: 'module-005',
  title: 'Global Issues & Human Rights',
  description:
  'Engage secondary students with pressing global challenges — refugee crises, economic inequality, and gender justice — through evidence-based inquiry and advocacy.',
  thumbnail: "https://images.unsplash.com/photo-1654727734708-6015e467bc1a",
  gradeBand: 'High School',
  lessonCount: 5,
  duration: '3 Weeks',
  topics: ['Human Rights', 'Global Issues'],
  sdgs: [1, 5, 8, 10, 16],
  price: 36,
  color: 'from-violet-500 to-violet-700',
  lessons: ['lesson-009', 'lesson-007', 'lesson-005', 'lesson-004', 'lesson-011']
},
{
  id: 'module-006',
  title: 'Water, Food & Climate: Interconnected Crises',
  description:
  'A cross-cutting middle school module connecting water access, food systems, and climate change through systems thinking and collaborative problem-solving.',
  thumbnail: "https://images.unsplash.com/photo-1724984423933-383c6b9796c9",
  gradeBand: 'Middle School',
  lessonCount: 5,
  duration: '3 Weeks',
  topics: ['Sustainability', 'Global Issues'],
  sdgs: [2, 6, 13, 14],
  price: 34,
  color: 'from-blue-500 to-blue-700',
  lessons: ['lesson-002', 'lesson-010', 'lesson-006', 'lesson-004', 'lesson-009']
}];


export const TESTIMONIALS = [
{
  id: 'testimonial-001',
  name: 'Priya Nair',
  role: '7th Grade Social Studies, Austin ISD',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_176f7c544-1767274192429.png",
  quote:
  'My students finally understand why global issues matter to them personally. The identity mapping unit sparked the most authentic classroom conversations I\'ve had in 8 years of teaching.',
  rating: 5,
  lessonUsed: 'Identity Mapping: Who Am I in the World?'
},
{
  id: 'testimonial-002',
  name: 'Marcus Webb',
  role: 'Elementary Curriculum Coordinator, Chicago Public Schools',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4d21463-1764671823131.png",
  quote:
  'We rolled out the Global Citizenship Foundations module across 12 classrooms. The pacing guides and ready-made materials saved our teachers hours of prep every week.',
  rating: 5,
  lessonUsed: 'Global Citizenship Foundations Module'
},
{
  id: 'testimonial-003',
  name: 'Amara Osei',
  role: '10th Grade English & Humanities, Philadelphia Academy',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7dee047-1768132059459.png",
  quote:
  'The Climate Justice unit is the most rigorous, culturally responsive resource I\'ve found anywhere. The Google Classroom integration made assigning it completely seamless.',
  rating: 5,
  lessonUsed: 'Climate Justice: Who Bears the Burden?'
}];


export const DASHBOARD_STATS = {
  downloadsUsed: 34,
  downloadsLimit: 50,
  favoritesCount: 18,
  lessonsAssigned: 12,
  planName: 'Teacher Pro',
  planRenewal: 'May 15, 2026',
  streak: 7
};

export const DOWNLOAD_HISTORY = [
{ month: 'Oct', downloads: 4 },
{ month: 'Nov', downloads: 7 },
{ month: 'Dec', downloads: 3 },
{ month: 'Jan', downloads: 9 },
{ month: 'Feb', downloads: 6 },
{ month: 'Mar', downloads: 11 },
{ month: 'Apr', downloads: 5 }];


export const TOPIC_DISTRIBUTION = [
{ topic: 'Sustainability', count: 18 },
{ topic: 'Identity & Culture', count: 14 },
{ topic: 'Global Issues', count: 22 },
{ topic: 'Human Rights', count: 11 },
{ topic: 'Global Citizenship', count: 16 }];


export const RECENT_ACTIVITY = [
{
  id: 'activity-001',
  action: 'Downloaded',
  lessonTitle: 'Climate Justice: Who Bears the Burden?',
  lessonId: 'lesson-004',
  timestamp: '2 hours ago',
  icon: 'ArrowDownTrayIcon'
},
{
  id: 'activity-002',
  action: 'Assigned to Google Classroom',
  lessonTitle: 'Identity Mapping: Who Am I in the World?',
  lessonId: 'lesson-003',
  timestamp: 'Yesterday',
  icon: 'AcademicCapIcon'
},
{
  id: 'activity-003',
  action: 'Favorited',
  lessonTitle: 'Refugee Journeys: Stories of Resilience and Hope',
  lessonId: 'lesson-005',
  timestamp: '3 days ago',
  icon: 'HeartIcon'
},
{
  id: 'activity-004',
  action: 'Viewed',
  lessonTitle: 'Clean Water Access: A Global Crisis in Our Classrooms',
  lessonId: 'lesson-002',
  timestamp: '4 days ago',
  icon: 'EyeIcon'
},
{
  id: 'activity-005',
  action: 'Downloaded',
  lessonTitle: 'What Is Global Citizenship? An Introduction for Young Learners',
  lessonId: 'lesson-001',
  timestamp: '1 week ago',
  icon: 'ArrowDownTrayIcon'
}];


export const LMS_ASSIGNMENTS = [
{
  id: 'assign-001',
  lessonTitle: 'Identity Mapping: Who Am I in the World?',
  platform: 'Google Classroom',
  class: 'Period 3 — 7th Grade Humanities',
  assignedDate: '04/14/2026',
  dueDate: '04/21/2026',
  submissions: '18/24',
  status: 'Active'
},
{
  id: 'assign-002',
  lessonTitle: 'Climate Justice: Who Bears the Burden?',
  platform: 'Canvas',
  class: 'AP Environmental Science',
  assignedDate: '04/10/2026',
  dueDate: '04/17/2026',
  submissions: '28/28',
  status: 'Completed'
},
{
  id: 'assign-003',
  lessonTitle: 'Refugee Journeys: Stories of Resilience and Hope',
  platform: 'Google Classroom',
  class: 'Period 5 — 8th Grade Social Studies',
  assignedDate: '04/07/2026',
  dueDate: '04/14/2026',
  submissions: '12/22',
  status: 'Past Due'
},
{
  id: 'assign-004',
  lessonTitle: 'What Is Global Citizenship?',
  platform: 'Schoology',
  class: 'Grade 4 — Room 12',
  assignedDate: '04/01/2026',
  dueDate: '04/08/2026',
  submissions: '20/20',
  status: 'Completed'
}];